import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import PostCard from "../components/PostCard";
import AvatarUploader from "../components/AvatarUploader";
import { useAuth } from "../lib/auth";
import { PencilSimpleLine, Check, X } from "@phosphor-icons/react";

function Initials({ name, color = "#E60012", size = 128 }) {
  return (
    <div className="rounded-full flex items-center justify-center font-display text-white tick-corners"
      style={{ width: size, height: size, background: color, fontWeight: 900, fontSize: size * 0.5 }} data-testid="profile-avatar">
      {name?.[0]?.toUpperCase()}
    </div>
  );
}

export default function Profile() {
  const { id } = useParams();
  const { user: me, refreshUser } = useAuth();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [u, p] = await Promise.all([
      api.get(`/users/${id}`),
      api.get(`/users/${id}/posts`),
    ]);
    setUser(u.data);
    setPosts(p.data);
    setForm({ name: u.data.name, bio: u.data.bio || "" });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!user) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING PROFILE...</div></AppShell>;

  const isMe = me && me.id === user.id;
  const avatarSrc = user.avatar_url?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`
    : user.avatar_url;

  const saveAvatar = async (url) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", { avatar_url: url });
      setUser(data);
      refreshUser();
    } finally { setBusy(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", { name: form.name, bio: form.bio });
      setUser(data);
      refreshUser();
      setEditing(false);
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto" data-testid="profile-page">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-4">
            {isMe ? (
              <AvatarUploader currentUrl={user.avatar_url} onUploaded={saveAvatar} size={160} label="Click to change picture" />
            ) : avatarSrc ? (
              <div className="w-40 h-40 rounded-full overflow-hidden border-2 border-[#27272A] tick-corners">
                <img src={avatarSrc} alt={user.name} className="w-full h-full object-cover" data-testid="profile-avatar" />
              </div>
            ) : (
              <Initials name={user.name} size={160} />
            )}
          </div>
          <div className="md:col-span-8">
            <div className="label-mono mb-2">MEMBER · {user.role.toUpperCase()}</div>

            {editing ? (
              <form onSubmit={saveProfile} className="mb-4" data-testid="profile-edit-form">
                <label className="label-mono block mb-1.5">DISPLAY NAME</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={80}
                  className="input mb-3" data-testid="profile-edit-name" />
                <label className="label-mono block mb-1.5">BIO</label>
                <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} maxLength={500}
                  className="input mb-4 resize-none" placeholder="What do you work on?" data-testid="profile-edit-bio" />
                <div className="flex gap-2">
                  <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid="profile-edit-save">
                    {busy ? <span className="spinner" /> : <><Check size={12} weight="bold" /> Save</>}
                  </button>
                  <button type="button" onClick={() => setEditing(false)} className="btn btn-ghost btn-sm" data-testid="profile-edit-cancel">
                    <X size={12} /> Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h1 className="font-display text-white" style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-0.035em" }} data-testid="profile-name">
                    {user.name}
                  </h1>
                  {isMe && (
                    <button onClick={() => setEditing(true)} className="btn btn-ghost btn-sm" data-testid="profile-edit-btn">
                      <PencilSimpleLine size={12} /> Edit profile
                    </button>
                  )}
                </div>
                {user.bio && <p className="text-zinc-300 mb-6 max-w-xl leading-relaxed" data-testid="profile-bio">{user.bio}</p>}
              </>
            )}

            <div className="grid grid-cols-3 border border-[#27272A] max-w-md">
              <div className="p-4 border-r border-[#27272A]">
                <div className="font-display text-2xl text-white" style={{ fontWeight: 900 }} data-testid="profile-level">LVL {user.level}</div>
                <div className="label-mono mt-1">Tier</div>
              </div>
              <div className="p-4 border-r border-[#27272A]">
                <div className="font-display text-2xl text-white" style={{ fontWeight: 900 }} data-testid="profile-points">{user.points}</div>
                <div className="label-mono mt-1">Points</div>
              </div>
              <div className="p-4">
                <div className="font-display text-2xl text-white" style={{ fontWeight: 900 }} data-testid="profile-posts-count">{posts?.length ?? 0}</div>
                <div className="label-mono mt-1">Posts</div>
              </div>
            </div>
          </div>
        </div>

        <div className="label-mono mb-4" style={{ color: "#E60012" }}>POSTS BY {user.name.toUpperCase()}</div>
        {posts === null ? (
          <div className="py-12 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING POSTS...</div>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-12 text-center text-zinc-400" data-testid="profile-empty">
            No posts yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5" data-testid="profile-posts">
            {posts.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}
